import { DepthLightCustom } from '@repo/go-icons';
import { TableCell } from '@repo/go-table';
import type { GridColDef } from '@repo/go-table';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { depthSortedParameters, nameDictionary, timeSortedParametersNoAutoRecognized } from '../../shared/baseParams';
import { Tag, type TStatus } from '../../shared/components/Tag/Tag';
import type { TimeZonesStore } from '../../shared/stores/TimeZonesStore';
import { convertDepthUnitValue } from '../../shared/utils';
import tableStyles from '../AlertsManagerTable.module.css';
import { TableHeader } from '../components/TableHeader/TableHeader';
import { UserInfo } from '../components/UserInfo';
import type { TIndexType, TRiskLevel, TSourceType, TOption } from '../types';
import { uniqueOptions } from '../utils';

import type { AutoAnomaliesService } from './AutoAnomaliesService';
import { FIELD_TO_AUTO_ANOMALIES_COLUMN_TITLE_KEY } from './FIELD_TO_AUTO_ANOMALIES_COLUMN_TITLE_KEY';
import type { TAutoAnomalyRowData } from './types';
import { AUTO_ANOMALIES_GROUPED_COLUMN_FIELDS } from './useColumnGroups';

const riskToStatus = (risk: TRiskLevel): TStatus => {
  switch (risk) {
    case 'HIGH_RISK':
      return 'red';
    case 'MED_RISK':
      return 'orange';
    case 'LOW_RISK':
      return 'blue';
    case 'NO_RISK':
      return 'gray';
  }
};

export const useAutoAnomaliesColumns = (
  timeZonesStore: TimeZonesStore,
  service: AutoAnomaliesService,
  isAltNames: boolean,
  defaultDepthUnit: 'm' | 'ft',
) => {
  const { t } = useTranslation();
  const getDateWithTimeZone = timeZonesStore.getDate;
  const scaleDepthUnit = service.settingsStore.scaleDepthUnit;
  const editing = service.uiState.editing;

  const riskLabels = useMemo(
    () => ({
      NO_RISK: t('alertsManagerWidget:riskLevelOptions.NO_RISK'),
      LOW_RISK: t('alertsManagerWidget:riskLevelOptions.LOW_RISK'),
      MED_RISK: t('alertsManagerWidget:riskLevelOptions.MED_RISK'),
      HIGH_RISK: t('alertsManagerWidget:riskLevelOptions.HIGH_RISK'),
    }),
    [t],
  );

  return useMemo((): GridColDef<TAutoAnomalyRowData>[] => {
    const baseColumn: Omit<GridColDef, 'field'> = {
      sortable: true,
      filterable: true,
      resizable: true,
      disableReorder: !editing,
      minWidth: 61,
      headerAlign: 'left',
      renderHeader: (params) => (
        <TableHeader
          field={params.field}
          textTKey={FIELD_TO_AUTO_ANOMALIES_COLUMN_TITLE_KEY[params.field] ?? params.field}
          tableSubHeader={AUTO_ANOMALIES_GROUPED_COLUMN_FIELDS.has(params.field)}
        />
      ),
      renderCell: (params) => {
        return <div style={{ padding: '0 10px' }}>{params.formattedValue}</div>;
      },
    };

    const numberColumn: Omit<GridColDef, 'field'> = {
      ...baseColumn,
      align: 'right',
      valueFormatter: (value) => {
        const number = Number(value);
        if (Number.isNaN(number) || !Number.isFinite(number)) {
          return value;
        }
        return `${Math.round(number * 100) / 100}`;
      },
    };

    function getSourceTypeOptions(source: TSourceType, indexType: TIndexType): TOption[] {
      if (source === 'GENERAL_PARAMETERS') {
        const parameters = indexType === 'LOG_TIME' ? timeSortedParametersNoAutoRecognized : depthSortedParameters;
        return parameters.map((parameter) => ({
          label: t(`alertsManagerWidget:baseParameters.${nameDictionary[parameter]}`),
          value: parameter,
        }));
      }
      if (source === 'AUTOSUPERVISOR_OPERATIONS') {
        return service.autosupervisorOperations.map((operation) => ({
          label: operation.data['name']?.toString() ?? '',
          value: operation.data.code as number,
        }));
      }
      if (source === 'AUTOSUPERVISOR_KPI_STATES') {
        return service.autosupervisorKPIstates.map((kpi) => ({
          label: kpi.data['name']?.toString() ?? '',
          value: kpi.data.code as number,
        }));
      }
      return [];
    }

    function getAllSourceTypeOptions(): TOption[] {
      return uniqueOptions([
        ...getSourceTypeOptions('GENERAL_PARAMETERS', 'LOG_TIME'),
        ...getSourceTypeOptions('GENERAL_PARAMETERS', 'LOG_DEPTH'),
        ...getSourceTypeOptions('AUTOSUPERVISOR_OPERATIONS', 'LOG_TIME'),
        ...getSourceTypeOptions('AUTOSUPERVISOR_KPI_STATES', 'LOG_TIME'),
      ]);
    }

    const eventZoned = (row: TAutoAnomalyRowData) => {
      const d = new Date(row.anomalyAt);
      if (Number.isNaN(d.valueOf())) {
        return null;
      }

      return getDateWithTimeZone(d.valueOf());
    };

    return [
      {
        field: 'eventDate',
        sortable: true,
        filterable: true,
        resizable: true,
        disableReorder: !editing,
        minWidth: 110,
        flex: 0.5,
        headerAlign: 'left',
        align: 'left',
        valueGetter: (_, row) => row.anomalyAt,
        renderHeader: (params) => (
          <TableHeader field={params.field} textTKey={FIELD_TO_AUTO_ANOMALIES_COLUMN_TITLE_KEY.eventDate} />
        ),
        renderCell(params) {
          const eventDate = eventZoned(params.row);
          return <TableCell>{eventDate ? format(eventDate, 'dd.MM.yyyy') : '-'}</TableCell>;
        },
      },
      {
        field: 'eventTime',
        sortable: true,
        filterable: true,
        resizable: true,
        disableReorder: !editing,
        minWidth: 100,
        flex: 0.5,
        headerAlign: 'left',
        align: 'left',
        valueGetter: (_, row) => row.anomalyAt,
        renderHeader: (params) => (
          <TableHeader field={params.field} textTKey={FIELD_TO_AUTO_ANOMALIES_COLUMN_TITLE_KEY.eventTime} />
        ),
        renderCell(params) {
          const eventDate = eventZoned(params.row);
          return <TableCell>{eventDate ? format(eventDate, 'HH:mm:ss') : '-'}</TableCell>;
        },
      },
      {
        ...baseColumn,
        field: 'wellboreId',
        type: 'singleSelect',
        valueOptions: service.wellbores.list.map((wellbore) => ({
          value: wellbore.id,
          label: wellbore.specialName,
        })),
        minWidth: 120,
      },
      {
        field: 'riskLevel',
        sortable: true,
        filterable: true,
        resizable: false,
        disableReorder: !editing,
        width: 120,
        minWidth: 120,
        maxWidth: 120,
        headerAlign: 'left',
        align: 'left',
        valueGetter: (_, row) => row.riskLevel,
        renderHeader: (params) => (
          <TableHeader field={params.field} textTKey={FIELD_TO_AUTO_ANOMALIES_COLUMN_TITLE_KEY.riskLevel} />
        ),
        renderCell(params) {
          const risk = params.value as TRiskLevel;
          return (
            <TableCell>
              <Tag status={riskToStatus(risk)} block style={{ width: '100%' }}>
                {riskLabels[risk] ?? risk}
              </Tag>
            </TableCell>
          );
        },
      },
      {
        ...baseColumn,
        field: 'createdBy',
        type: 'string',
        minWidth: 120,
        editable: false,
        renderCell(params) {
          return (
            <UserInfo
              createdBy={params.row.createdBy}
              createdAt={params.row.createdAt}
              timeZonesStore={timeZonesStore}
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'anomalyTypeId',
        type: 'singleSelect',
        minWidth: 120,
        valueOptions: service.anomalyTypes.map((anomalyType) => ({
          value: anomalyType.id,
          label: anomalyType.data['name']?.toString() ?? '',
        })),
      },
      {
        ...baseColumn,
        field: 'anomalySubtypeId',
        type: 'singleSelect',
        minWidth: 120,
        valueOptions: service.anomalySubtypes.map((anomalySubtype) => ({
          value: anomalySubtype.id,
          label: anomalySubtype.data['name']?.toString() ?? '',
        })),
      },
      {
        ...baseColumn,
        field: 'indexType',
        type: 'singleSelect',
        minWidth: 120,
        valueOptions: [
          { value: 'LOG_TIME', label: t('alertsManagerWidget:autoAnomalies.indexFormat.time') },
          { value: 'LOG_DEPTH', label: t('alertsManagerWidget:autoAnomalies.indexFormat.depth') },
        ],
      },
      {
        ...baseColumn,
        field: 'sourceType',
        type: 'singleSelect',
        minWidth: 120,
        valueOptions: ['GENERAL_PARAMETERS', 'AUTOSUPERVISOR_OPERATIONS', 'AUTOSUPERVISOR_KPI_STATES'].map(
          (source) => ({
            value: source,
            label: t(`alertsManagerWidget:autoAnomalies.sourceDataType.${source}`),
          }),
        ),
      },
      {
        ...baseColumn,
        field: 'sourceOption',
        type: 'singleSelect',
        minWidth: 120,
        valueOptions: (params) =>
          params.row && params.row.sourceType
            ? getSourceTypeOptions(params.row.sourceType, params.row.indexType)
            : getAllSourceTypeOptions(),
      },
      {
        ...baseColumn,
        field: 'bitDepth',
        type: 'number',
        valueFormatter(_value) {
          if (_value == null) {
            return '-';
          }

          const value = Number(_value);

          if (!Number.isNaN(value)) {
            return Math.round(convertDepthUnitValue(value, defaultDepthUnit, scaleDepthUnit) * 100) / 100;
          }

          return '-';
        },
        renderHeader: (params) => (
          <TableHeader
            field={params.field}
            textTKey={FIELD_TO_AUTO_ANOMALIES_COLUMN_TITLE_KEY.bitDepth}
            unit="m"
            icon={<DepthLightCustom />}
          />
        ),
      },
      {
        ...baseColumn,
        field: 'holeDepth',
        type: 'number',
        valueFormatter(_value) {
          if (_value == null) {
            return '-';
          }

          const value = Number(_value);

          if (!Number.isNaN(value)) {
            return Math.round(convertDepthUnitValue(value, defaultDepthUnit, scaleDepthUnit) * 100) / 100;
          }

          return '-';
        },
        renderHeader: (params) => (
          <TableHeader
            field={params.field}
            textTKey={FIELD_TO_AUTO_ANOMALIES_COLUMN_TITLE_KEY.holeDepth}
            unit="m"
            icon={<DepthLightCustom />}
          />
        ),
      },
      {
        ...baseColumn,
        field: 'limitType',
        type: 'singleSelect',
        valueOptions: [
          'MIN_LIMIT',
          'MAX_LIMIT',
          'MIN_LIMIT_PARAMETER',
          'MAX_LIMIT_PARAMETER',
          'MIN_DURATION',
          'MAX_DURATION',
          'DELTA',
        ].map((limitType) => ({
          value: limitType,
          label: t(`alertsManagerWidget:autoAnomalies.limitType.${limitType}`),
        })),
      },
      {
        ...baseColumn,
        field: 'unitId',
        type: 'singleSelect',
        valueOptions: service.unitsList.map((unit) => ({
          value: unit.id,
          label: unit.data['abbreviation']?.toString() ?? '',
        })),
      },
      {
        ...numberColumn,
        field: 'valueFact',
        type: 'number',
      },
      {
        ...numberColumn,
        field: 'valueLimit',
        type: 'number',
      },
      {
        ...numberColumn,
        field: 'valueAbsoluteDiff',
        type: 'number',
      },
      {
        ...baseColumn,
        field: 'valueRelativeDiff',
        type: 'number',
        align: 'right',
        valueFormatter: (value) => {
          const number = Number(value);
          if (Number.isNaN(number) || !Number.isFinite(number)) {
            return value;
          }
          return `${Math.round(number * 100) / 100}%`;
        },
      },
      {
        ...baseColumn,
        field: 'notification',
        type: 'string',
      },
      {
        ...baseColumn,
        field: 'recommendation',
        type: 'string',
      },
      {
        ...baseColumn,
        field: 'referenceParameterName',
        type: 'string',
        valueGetter: (_, row) => {
          const referenceParameter = (row.minimumReferenceCurve ?? row.maximumReferenceCurve)?.parameter;

          if (!referenceParameter) {
            return '';
          }

          return isAltNames && referenceParameter.generalName
            ? referenceParameter.generalName
            : referenceParameter.displayMnemonic;
        },
      },
      {
        ...baseColumn,
        field: 'stageId',
        cellClassName: tableStyles.tableCellPurple,
        type: 'singleSelect',
        valueOptions: service.workClassifierStageTypes.map((stage) => ({
          value: stage.id,
          label: stage.data['name']?.toString() ?? '',
        })),
      },
      {
        ...baseColumn,
        field: 'wellboreTypeId',
        cellClassName: tableStyles.tableCellPurple,
        type: 'singleSelect',
        valueOptions: service.wellboreTypes.map((wellbore) => ({
          value: wellbore.id,
          label: wellbore.data['customName']?.toString() ?? '',
        })),
      },
      {
        ...baseColumn,
        field: 'trajectoryTypeId',
        cellClassName: tableStyles.tableCellPurple,
        type: 'singleSelect',
        valueOptions: service.trajectoryTypes.map((trajectory) => ({
          value: trajectory.id,
          label: trajectory.data['name']?.toString() ?? '',
        })),
      },
      {
        ...baseColumn,
        field: 'sectionTypeId',
        cellClassName: tableStyles.tableCellPurple,
        type: 'singleSelect',
        valueOptions: service.sectionTypes.map((section) => ({
          value: section.id,
          label: section.data['name']?.toString() ?? '',
        })),
      },
      {
        ...baseColumn,
        field: 'holeType',
        cellClassName: tableStyles.tableCellPurple,
        type: 'singleSelect',
        valueOptions: [
          { value: 'cased', label: t('alertsManagerWidget:autoAnomalies.holeType.cased') },
          { value: 'open', label: t('alertsManagerWidget:autoAnomalies.holeType.open') },
        ],
      },
      {
        ...baseColumn,
        field: 'stratUnitTypeId',
        cellClassName: tableStyles.tableCellPurple,
        type: 'singleSelect',
        valueOptions: service.stratUnitTypes.map((stratUnit) => ({
          value: stratUnit.id,
          label: stratUnit.data['name']?.toString() ?? '',
        })),
      },
      {
        ...baseColumn,
        field: 'reservoirId',
        cellClassName: tableStyles.tableCellPurple,
        type: 'singleSelect',
        valueOptions: service.reservoirTypes.map((reservoir) => ({
          value: reservoir.id,
          label: reservoir.data['name']?.toString() ?? '',
        })),
      },
      {
        ...baseColumn,
        field: 'runTypeId',
        cellClassName: tableStyles.tableCellPurple,
        type: 'singleSelect',
        valueOptions: service.runTypes.map((run) => ({
          value: run.id,
          label: run.data['name']?.toString() ?? '',
        })),
      },
      {
        ...baseColumn,
        field: 'operationId',
        cellClassName: tableStyles.tableCellPurple,
        type: 'singleSelect',
        valueOptions: service.autosupervisorOperations.map((operation) => ({
          value: operation.id,
          label: operation.data['name']?.toString() ?? '',
        })),
      },
    ];
  }, [
    service.wellbores.list,
    service.anomalyTypes,
    service.anomalySubtypes,
    service.unitsList,
    service.workClassifierStageTypes,
    service.wellboreTypes,
    service.trajectoryTypes,
    service.sectionTypes,
    service.stratUnitTypes,
    service.reservoirTypes,
    service.runTypes,
    service.autosupervisorOperations,
    service.autosupervisorKPIstates,
    t,
    getDateWithTimeZone,
    scaleDepthUnit,
    isAltNames,
    defaultDepthUnit,
    riskLabels,
    timeZonesStore,
    editing,
  ]);
};
